import React, { forwardRef } from 'react'
import { Select } from '@mantine/core'

import { ReadWaiveAnswer } from '@/types'

type Props = Omit<
  React.ComponentProps<typeof Select>,
  'value' | 'onChange' | 'data'
> & {
  value: ReadWaiveAnswer
  onChange(newValue: AnsweredReadWaive): void
}

/**
 * The answers a witness can give. "No answer" is what an unasked question
 * leaves behind rather than something anyone picks, so it is neither offered
 * here nor labelled anywhere: the select shows empty, and the screens that
 * report an answer show nothing at all.
 */
export type AnsweredReadWaive = Exclude<ReadWaiveAnswer, 'no_answer'>

export const READ_WAIVE_LABELS: Record<AnsweredReadWaive, string> = {
  read: 'Read',
  waive: 'Waive',
}

const DATA = (Object.keys(READ_WAIVE_LABELS) as AnsweredReadWaive[]).map(
  value => ({ value, label: READ_WAIVE_LABELS[value] }),
)

const isAnswer = (value: string | null): value is AnsweredReadWaive =>
  value !== null && value in READ_WAIVE_LABELS

export const ReadWaiveSelect = forwardRef<HTMLInputElement, Props>(
  ({ value, onChange, ...rest }, ref) => (
    <Select
      ref={ref}
      value={value === 'no_answer' ? null : value}
      onChange={newValue => {
        if (isAnswer(newValue)) {
          onChange(newValue)
        }
      }}
      data={DATA}
      placeholder="Select an answer"
      allowDeselect={false}
      {...rest}
    />
  ),
)

ReadWaiveSelect.displayName = 'ReadWaiveSelect'
